/**
 * InterLogs Trade — News Stream & Catch-up Worker
 * Connects to SSE endpoint and orchestrates feed updates with the Window Manager.
 */

import { NewsItem, CategoryType, SentimentType } from './types';
import { NewsWindowManager } from './news_window';

export class NewsStreamController {
  private windowManager: NewsWindowManager;
  private container: HTMLElement;
  private allNews: NewsItem[] = [];
  private knownIds: Set<string> = new Set();
  private currentFilter: string = 'all';
  private eventSource: EventSource | null = null;
  private catchupTimer: number | null = null;

  constructor(windowManager: NewsWindowManager, containerId: string = 'news-feed-container') {
    this.windowManager = windowManager;
    const el = document.getElementById(containerId);
    if (!el) {
      throw new Error(`Container #${containerId} not found`);
    }
    this.container = el;

    this.setupFilterButtons();
    this.setupDrawerToggle();
    this.fetchInitialNews();
    this.connectEventSource();

    // 6-second catch-up sync poller
    this.catchupTimer = window.setInterval(() => {
      this.fetchCatchupNews();
    }, 6000);
  }

  private setupFilterButtons(): void {
    const filterBtns = document.querySelectorAll('.wire-filter-btn');
    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.currentFilter = btn.getAttribute('data-filter') || 'all';
        this.renderFilteredFeed();
      });
    });
  }

  private setupDrawerToggle(): void {
    const toggleBtn = document.getElementById('btn-toggle-wire');
    const wireCol = document.getElementById('wire-column');
    if (toggleBtn && wireCol) {
      toggleBtn.addEventListener('click', () => {
        wireCol.classList.toggle('open');
        toggleBtn.classList.toggle('active', wireCol.classList.contains('open'));
      });
    }
  }

  public async fetchInitialNews(): Promise<void> {
    try {
      const res = await fetch('/api/trade/news?limit=50');
      if (!res.ok) return;
      const data = await res.json();
      if (data.news && Array.isArray(data.news)) {
        this.container.innerHTML = '';
        for (const item of data.news) {
          if (!this.knownIds.has(item.id)) {
            this.knownIds.add(item.id);
            this.allNews.push(item);
          }
        }
        this.renderFilteredFeed();
        this.updateWireCounter();
      }
    } catch (err) {
      console.warn('Initial news fetch error:', err);
    }
  }

  public async fetchCatchupNews(): Promise<void> {
    try {
      const res = await fetch('/api/trade/news?limit=25');
      if (!res.ok) return;
      const data = await res.json();
      if (data.news && Array.isArray(data.news)) {
        // Traverse backwards so newest appear at top
        for (let i = data.news.length - 1; i >= 0; i--) {
          const item = data.news[i];
          if (!this.knownIds.has(item.id)) {
            this.onNewIncomingNews(item);
          }
        }
      }
    } catch (_) {}
  }

  public onNewIncomingNews(item: NewsItem): void {
    if (!item || !item.id || this.knownIds.has(item.id)) return;
    this.knownIds.add(item.id);
    this.allNews.unshift(item); // Prepend to master news list

    this.updateWireCounter();

    // Pulse live badge
    const liveBadge = document.querySelector('.wire-badge-live');
    if (liveBadge) {
      liveBadge.classList.add('pulse-active');
      setTimeout(() => liveBadge.classList.remove('pulse-active'), 1200);
    }

    // If matches current active filter, prepend to DOM
    if (this.matchesFilter(item, this.currentFilter)) {
      const skeleton = this.container.querySelector('.news-loading-skeleton');
      if (skeleton) skeleton.remove();

      const card = this.windowManager.createCardElement(item, true);
      this.container.prepend(card);

      // Limit DOM children to 80 nodes to preserve performance
      if (this.container.children.length > 80) {
        // Do not remove open windows if possible
        const lastChild = this.container.lastElementChild as HTMLElement;
        if (lastChild && !this.windowManager.hasOpenWindow(lastChild.dataset.id || '')) {
          lastChild.remove();
        }
      }
    }
  }

  public matchesFilter(item: NewsItem, filter: string): boolean {
    if (!filter || filter === 'all') return true;
    if (filter === 'commodities' || filter === 'macro' || filter === 'crypto') {
      return (item.category || '').toLowerCase() === filter;
    }
    if (filter === 'positive' || filter === 'negative' || filter === 'neutral') {
      return (item.sentiment || '').toLowerCase() === filter;
    }
    return true;
  }

  public renderFilteredFeed(): void {
    const filtered = this.allNews.filter(item => this.matchesFilter(item, this.currentFilter));

    if (filtered.length === 0) {
      this.container.innerHTML = `<div class="news-loading-skeleton">No news items found under "${this.currentFilter.toUpperCase()}".</div>`;
      return;
    }

    this.container.innerHTML = '';
    const fragment = document.createDocumentFragment();
    for (const item of filtered.slice(0, 50)) {
      const card = this.windowManager.createCardElement(item, false);
      fragment.appendChild(card);
    }
    this.container.appendChild(fragment);
  }

  private updateWireCounter(): void {
    const counter = document.getElementById('wire-counter');
    if (counter) {
      counter.textContent = String(this.allNews.length);
    }
  }

  private connectEventSource(): void {
    try {
      if (this.eventSource) {
        this.eventSource.close();
      }

      this.eventSource = new EventSource('/api/trade/stream');

      this.eventSource.addEventListener('market_news', (e: MessageEvent) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.news) {
            this.onNewIncomingNews(msg.news);
          }
        } catch (err) {
          console.error('SSE news parse error:', err);
        }
      });

      this.eventSource.onerror = () => {
        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = null;
        }
        setTimeout(() => this.connectEventSource(), 4000);
      };
    } catch (e) {
      console.warn('SSE unsupported, relying on catchup polling');
    }
  }

  public destroy(): void {
    if (this.eventSource) {
      this.eventSource.close();
    }
    if (this.catchupTimer !== null) {
      clearInterval(this.catchupTimer);
    }
  }
}
